/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from 'axios';
import { getAuthToken } from '@nohara-dxdev/react-oidc';

import { tokenStorage } from '@/utils/storage';
import { ApiResponse, ApiErrorResponse, PaginatedResponse } from '@/types/api';
import { getEndPoint } from '@/utils/env';
// Extend InternalAxiosRequestConfig for refresh retry and error normalization opt-out
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  /** See `axios-augment.d.ts` — opt out of global error shaping (e.g. blob download errors). */
  skipGlobalErrorNormalization?: boolean;
}

// --- AXIOS INSTANCE CONFIGURATION ---
// Get base URL from environment variable for flexibility between dev and production
const BASE_URL_ENDPOINT = getEndPoint();

// Validate environment variable
if (!BASE_URL_ENDPOINT) {
  throw new Error('API endpoint configuration is missing');
}

// Fallback URL for development
const FALLBACK_URL = 'http://localhost:3000/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL_ENDPOINT || FALLBACK_URL,
  timeout: 15000, // 15 seconds for better reliability
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable credentials for CORS
  withCredentials: false,
});

// --- INTERCEPTORS: GLOBAL REQUEST AND RESPONSE HANDLING ---

// Request retry configuration
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  for (const { resolve, reject } of failedQueue) {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  }
  failedQueue = [];
};

// Error message mapping to reduce switch complexity
const getErrorMessage = (
  status: number,
  serverMessage: string,
  data?: any
): { message: string; errors?: any } => {
  const errorMessages: Record<number, string> = {
    400: '送信されたデータが無効です。',
    403: 'この操作を実行する権限がありません。',
    404: '要求されたリソースが見つかりません。',
    422: '入力データに問題があります。',
    429: 'リクエストが多すぎます。しばらく待ってから再試行してください。',
    500: 'サーバーでエラーが発生しました。',
    502: 'サーバーが一時的に利用できません。',
    503: 'サービスが一時的に利用できません。',
  };

  const defaultMessage = errorMessages[status] || `エラーが発生しました: ${status}`;
  const message = serverMessage || defaultMessage;

  const result: { message: string; errors?: any } = { message };

  // Add validation errors for specific status codes
  if ((status === 400 || status === 422) && data?.errors) {
    result.errors = data.errors;
  }

  return result;
};

// Token refresh logic extracted to reduce complexity
const handleTokenRefresh = async (originalRequest: ExtendedAxiosRequestConfig): Promise<any> => {
  if (isRefreshing) {
    // Add to queue while refreshing
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((token) => {
        if (typeof token !== 'string') {
          throw new Error('Token refresh failed');
        }
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      })
      .catch((err) => {
        throw err instanceof Error ? err : new Error(String(err));
      });
  }

  isRefreshing = true;

  try {
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      const response = await axios.post(`${apiClient.defaults.baseURL}/auth/refresh`, {
        refreshToken,
      });

      const { accessToken } = response.data.data;
      tokenStorage.setAccessToken(accessToken);

      processQueue(null, accessToken);
      isRefreshing = false;

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(originalRequest);
    } else {
      // No refresh token, redirect to login
      tokenStorage.removeAccessToken();
      globalThis.location.href = '/';
    }
  } catch (refreshError) {
    processQueue(refreshError, null);
    isRefreshing = false;
    tokenStorage.clearTokens();
    globalThis.location.href = '/';
  }
};

/** Plain JSON error bodies only. Blob error bodies are handled by callers that set `skipGlobalErrorNormalization`. */
const normalizeAxiosErrorBody = (data: unknown): Record<string, unknown> => {
  if (data != null && typeof data === 'object' && !(data instanceof Blob)) {
    return data as Record<string, unknown>;
  }
  return {};
};

const firstErrorMessageFromBody = (data: Record<string, unknown>): string => {
  const errors = data.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0] as { message?: string };
    if (typeof first?.message === 'string' && first.message) return first.message;
  }
  const top = data.message;
  return typeof top === 'string' ? top : '';
};

// Create error response based on error type
const createErrorResponse = (error: any): ApiErrorResponse => {
  const errorResponse: ApiErrorResponse = {
    success: false,
    message: '不明なエラーが発生しました。もう一度お試しください。',
    statusCode: 500,
  };

  if (error.response) {
    // Server error (has response)
    const { status } = error.response;
    const data = normalizeAxiosErrorBody(error.response.data);
    errorResponse.statusCode = status;
    const serverMessage = firstErrorMessageFromBody(data);

    if (status === 401) {
      errorResponse.message =
        serverMessage || 'アクセス権限がありません。再度ログインしてください。';
    } else {
      const errorInfo = getErrorMessage(status, serverMessage, data);
      errorResponse.message = errorInfo.message;
      if (errorInfo.errors) {
        errorResponse.errors = errorInfo.errors;
      }
    }
  } else if (error.request) {
    // Network error (no response received)
    errorResponse.message = 'サーバーに接続できません。ネットワーク接続を確認してください。';
    errorResponse.statusCode = 0;
  } else {
    // Request setup error
    errorResponse.message = 'リクエストの設定中にエラーが発生しました。';
    errorResponse.statusCode = 0;
  }

  return errorResponse;
};

// 1. Request Interceptor: Intercept before sending requests
apiClient.interceptors.request.use(
  async (config: ExtendedAxiosRequestConfig) => {
    // First try localStorage token, then fallback to OIDC session token
    let token = tokenStorage.getAccessToken();

    // If no token in localStorage, try to get OIDC session token
    if (!token) {
      try {
        token = (await getAuthToken()) || null;
      } catch {
        // OIDC not available, continue without token
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    // Handle request errors
    throw error instanceof Error ? error : new Error(String(error));
  }
);

// 2. Response Interceptor: Intercept when receiving responses
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<any>>): AxiosResponse<any, any> => {
    // Return the full response structure, don't extract data
    // This allows consumers to access success, message, and data
    return response;
  },

  // 2.2. Handle error responses (status codes outside 2xx range)
  async (error) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle 401 token refresh logic
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      return handleTokenRefresh(originalRequest);
    }

    if (originalRequest.skipGlobalErrorNormalization) {
      throw error instanceof Error ? error : new Error(String(error));
    }

    // Create error response using helper function
    const errorResponse = createErrorResponse(error);

    // Throw the normalized error object
    const rejectionError = new Error(errorResponse.message);
    Object.assign(rejectionError, errorResponse);
    throw rejectionError;
  }
);

// --- UTILITY API METHODS ---
// Makes API calls simpler, just pass endpoint and data

const api = {
  // Basic CRUD operations
  get: <T>(
    endpoint: string,
    params?: object,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.get(endpoint, { params, ...config }),

  post: <T>(
    endpoint: string,
    data?: object,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.post(endpoint, data, config),

  put: <T>(
    endpoint: string,
    data?: object,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.put(endpoint, data, config),

  patch: <T>(
    endpoint: string,
    data?: object,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.patch(endpoint, data, config),

  delete: <T>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> => apiClient.delete(endpoint, config),

  // Paginated requests
  getPaginated: <T>(
    endpoint: string,
    params?: { page?: number; limit?: number; [key: string]: any }
  ): Promise<AxiosResponse<PaginatedResponse<T>>> => apiClient.get(endpoint, { params }),

  // Form data requests
  postFormData: <T>(
    endpoint: string,
    formData: FormData,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ApiResponse<T>>> =>
    apiClient.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config,
    }),

  // Request with custom headers
  request: <T>(config: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> =>
    apiClient.request(config),

  // Cancel token for request cancellation
  createCancelToken: () => axios.CancelToken.source(),
};

// Export both the api object and the raw axios instance
export { apiClient };
export default api;
